import React, { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { ImageUpload } from "@/components/admin/ImageUpload";
import { Plus, Trash, PencilSimple, X, FloppyDisk, Star } from "@phosphor-icons/react";

const field = "w-full rounded-md border border-slate-200 px-4 py-2.5 text-sm outline-none focus:border-teal focus:ring-2 focus:ring-teal/30";
const EMPTY = { name: "", image: "", order: 100, featured: false };

function CategoryRow({ c, onToggle, onEdit, onDelete }) {
  return (
    <div className="flex items-center justify-between gap-3 rounded-xl border border-slate-200 bg-white p-4" data-testid={`admin-category-${c.id}`}>
      <div className="flex items-center gap-3">
        {c.image && <img src={c.image} alt={c.name} className="h-10 w-10 rounded-md object-cover" />}
        <p className="font-heading text-sm font-semibold text-ink">{c.name}</p>
      </div>
      <div className="flex items-center gap-3">
        <button onClick={() => onToggle(c)} data-testid={`toggle-featured-${c.id}`} title="Toggle featured" className={c.featured ? "text-amber-400" : "text-slate-300 hover:text-amber-400"}>
          <Star size={18} weight={c.featured ? "fill" : "regular"} />
        </button>
        <button onClick={() => onEdit(c)} data-testid={`edit-category-${c.id}`} className="text-slate-400 hover:text-teal"><PencilSimple size={18} /></button>
        <button onClick={() => onDelete(c.id)} data-testid={`delete-category-${c.id}`} className="text-slate-400 hover:text-red-600"><Trash size={18} /></button>
      </div>
    </div>
  );
}

export function CategoriesManager() {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState(EMPTY);
  const [editId, setEditId] = useState(null);
  const load = () => api.get("/admin/product-categories").then((r) => setItems(r.data)).catch((e) => console.error("Failed to load categories:", e));
  useEffect(() => { load(); }, []);

  const reset = () => { setForm(EMPTY); setEditId(null); };

  const submit = async (e) => {
    e.preventDefault();
    const payload = { ...form, order: Number(form.order) };
    try {
      if (editId) { await api.put(`/admin/product-categories/${editId}`, payload); toast.success("Category updated"); }
      else { await api.post("/admin/product-categories", payload); toast.success("Category added"); }
      reset(); load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const edit = (c) => {
    setEditId(c.id);
    setForm({ name: c.name, image: c.image || "", order: c.order || 100, featured: !!c.featured });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const toggleFeatured = async (c) => {
    try { await api.put(`/admin/product-categories/${c.id}`, { featured: !c.featured }); load(); }
    catch { toast.error("Update failed"); }
  };

  const del = async (id) => {
    try { await api.delete(`/admin/product-categories/${id}`); toast.success("Deleted"); load(); }
    catch { toast.error("Delete failed"); }
  };

  return (
    <div className="grid gap-6 lg:grid-cols-2" data-testid="admin-categories-manager">
      <form onSubmit={submit} className="space-y-3 rounded-xl border border-slate-200 bg-white p-6" data-testid="admin-category-form">
        <div className="flex items-center justify-between">
          <h3 className="font-heading text-lg font-semibold text-ink">{editId ? "Edit category" : "Add product category"}</h3>
          {editId && <button type="button" onClick={reset} className="flex items-center gap-1 text-xs text-slate-500 hover:text-ink"><X size={14} /> Cancel</button>}
        </div>
        <input required placeholder="Category name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="category-name" className={field} />
        <input placeholder="Image URL (optional)" value={form.image} onChange={(e) => setForm({ ...form, image: e.target.value })} className={field} />
        <ImageUpload value={form.image} onChange={(url) => setForm((f) => ({ ...f, image: url }))} label="Upload category image" testid="category-image-upload" />
        <input type="number" placeholder="Order" value={form.order} onChange={(e) => setForm({ ...form, order: e.target.value })} className={field} />
        <label className="flex items-center gap-2 text-sm text-slate-600"><input type="checkbox" checked={form.featured} onChange={(e) => setForm({ ...form, featured: e.target.checked })} data-testid="category-featured" /> Featured on homepage</label>
        <button type="submit" data-testid="category-submit" className="flex items-center gap-2 rounded-md bg-ink px-5 py-2.5 text-sm font-semibold text-white hover:bg-ink-800">
          {editId ? <><FloppyDisk size={16} weight="bold" /> Save changes</> : <><Plus size={16} weight="bold" /> Add</>}
        </button>
      </form>
      <div className="space-y-3">
        <h3 className="font-heading text-lg font-semibold text-ink">Categories ({items.length})</h3>
        {items.map((c) => (
          <CategoryRow key={c.id} c={c} onToggle={toggleFeatured} onEdit={edit} onDelete={del} />
        ))}
        {items.length === 0 && <p className="rounded-xl border border-dashed border-slate-300 bg-white p-8 text-center text-sm text-slate-400">No categories yet.</p>}
      </div>
    </div>
  );
}
